import React, { useState, useEffect, useRef } from 'react';
import { Transition } from '@headlessui/react';
import {
  MenuAlt2Icon,
  XIcon,
  HomeIcon,
  UserGroupIcon,
  ShoppingBagIcon,
} from '@heroicons/react/outline';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@headlessui/react';

import * as Dialog from '@radix-ui/react-dialog';

import { Link, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/context/AuthContext';
import useCustomerAuthCheck from '../auth/hooks/useCustomerAuthCheck';
import LoginDialog from './LoginDialog';
import axios from 'axios';
import CustomerSidebar from './CustomerSidebar';
import { BrainCircuit } from 'lucide-react';

import {
  LayoutDashboard,
  MessageSquare,
  BarChart3,
  Settings,
  HelpCircle,
  Menu,
  Globe,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const CustomerHome = ({ children }) => {
  const location = useLocation();
  const { restaurantId } = useParams();
  const isRestaurantDetailsPage = location.pathname === `/${restaurantId}`;

  const [isSidebarOpen, setIsSidebarOpen] = useState(!isRestaurantDetailsPage);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Add resize handler
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        // md breakpoint
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    {
      icon: <LayoutDashboard className='w-5 h-5' />,
      title: 'My Subscriptions',
      path: `/subscriptions`,
    },
    {
      icon: <MessageSquare className='w-5 h-5' />,
      title: 'My Playlists',
      path: '/playlists',
    },
    {
      icon: <Settings className='w-5 h-5' />,
      title: 'Settings',
      path: `/settings`,
    },
    {
      icon: <BarChart3 className='w-5 h-5' />,
      title: 'Business',
      path: `/owner/dashboard`,
    },
  ];

  const SidebarContent = ({ mobile = false }) => {
    const [showTopGradient, setShowTopGradient] = useState(false);
    const [showBottomGradient, setShowBottomGradient] = useState(false);
    const navRef = useRef(null);

    const handleScroll = () => {
      if (navRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = navRef.current;
        setShowTopGradient(scrollTop > 20);
        setShowBottomGradient(scrollTop + clientHeight < scrollHeight - 20);
      }
    };

    useEffect(() => {
      handleScroll();
    }, []);

    return (
      <div className='flex flex-col h-full'>
        {/* Fixed header section */}
        <div className='p-6 flex items-center justify-between shrink-0 relative z-10 bg-white'>
          <Link href='/'>
            <h1
              className={`font-bold whitespace-nowrap transition-all duration-300 overflow-hidden
              ${
                !isSidebarOpen && !mobile
                  ? 'w-0 opacity-0'
                  : 'w-auto opacity-100'
              }
              text-2xl flex items-center gap-2`}
            >
              <BrainCircuit className='w-8 h-8 text-primary' />
              <div className='flex flex-col'>
                <div className='flex items-center '>
                  <span className='text-primary'>Twi</span>
                  <span className='text-gray-600'>qli</span>
                </div>
                <span className='text-xs text-gray-500 -mt-1'>
                  Discover new places
                </span>
              </div>
            </h1>
          </Link>
          {!mobile && isSidebarOpen && (
            <button
              onClick={() => setIsSidebarOpen(false)}
              className='p-2 rounded-lg hover:bg-gray-100'
            >
              <ChevronLeft />
            </button>
          )}
          {mobile && (
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className='p-2 rounded-lg hover:bg-gray-100'
            >
              <XIcon className='w-6 h-6' />
            </button>
          )}
        </div>

        {/* Navigation container with gradients */}
        <div className='flex-1 relative min-h-0'>
          {/* Top gradient */}
          <div
            className={`absolute top-0 left-0 right-0 h-12 z-10 pointer-events-none
            bg-gradient-to-b from-white to-transparent
            transition-opacity duration-200
            ${showTopGradient ? 'opacity-100' : 'opacity-0'}`}
          />

          {/* Scrollable navigation */}
          <nav
            ref={navRef}
            onScroll={handleScroll}
            className='h-full overflow-y-auto relative'
          >
            <ul className='space-y-1 px-4 pb-4'>
              {menuItems.map((item, index) => (
                <li key={item.href}>
                  <Link
                    key={index}
                    to={item.path}
                    className={`flex items-center justify-start 
                    gap-3 px-4 py-3 rounded-lg transition-colors
                    ${
                      location.pathname === item.path
                        ? 'bg-primary text-white'
                        : 'hover:bg-gray-100'
                    }
                    ${!isSidebarOpen && !mobile ? 'hidden' : ''}`}
                  >
                    {React.cloneElement(item.icon, {
                      className: 'w-6 h-6',
                    })}
                    <span
                      className={`whitespace-nowrap transition-all duration-300 overflow-hidden
                      ${
                        !isSidebarOpen && !mobile
                          ? 'w-0 opacity-0'
                          : 'w-auto opacity-100'
                      }`}
                    >
                      {item.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Bottom gradient */}
          <div
            className={`absolute bottom-0 left-0 right-0 h-12 z-10 pointer-events-none
            bg-gradient-to-t from-white to-transparent
            transition-opacity duration-200
            ${showBottomGradient ? 'opacity-100' : 'opacity-0'}`}
          />
        </div>
      </div>
    );
  };

  // const SidebarContent = ({ mobile = false }) => (
  //   <div className='flex flex-col h-full'>
  //     {/* Fixed header section */}
  //     <div className='p-6 flex items-center justify-between shrink-0'>
  //       <Link href='/'>
  //         <h1
  //           className={`font-bold whitespace-nowrap transition-all duration-300 overflow-hidden
  //           ${
  //             !isSidebarOpen && !mobile ? 'w-0 opacity-0' : 'w-auto opacity-100'
  //           }
  //           text-2xl flex items-center gap-2`}
  //         >
  //           <BrainCircuit className='w-8 h-8 text-primary' />
  //           <div className='flex flex-col'>
  //             <div className='flex items-center gap-1'>
  //               <span className='text-primary'>Interview</span>
  //               <span className='text-gray-600'>AI</span>
  //             </div>
  //             <span className='text-xs text-gray-500 -mt-1'>mock</span>
  //           </div>
  //         </h1>
  //       </Link>
  //       {!mobile && isSidebarOpen && (
  //         <button
  //           onClick={() => setIsSidebarOpen(false)}
  //           className='p-2 rounded-lg hover:bg-gray-100'
  //         >
  //           <ChevronLeft />
  //         </button>
  //       )}
  //       {mobile && (
  //         <button
  //           onClick={() => setIsMobileMenuOpen(false)}
  //           className='p-2 rounded-lg hover:bg-gray-100'
  //         >
  //           <XIcon className='w-6 h-6' />
  //         </button>
  //       )}
  //     </div>

  //     {/* Scrollable navigation section */}
  //     <nav className='flex-1 overflow-y-auto'>
  //       <ul className='space-y-1 px-4 pb-4'>
  //         {menuItems.map((item, index) => (
  //           <li key={item.href}>
  //             <Link
  //               key={index}
  //               to={item.path}
  //               className={`flex items-center justify-start
  //               gap-3 px-4 py-3 rounded-lg transition-colors
  //               ${
  //                 location.pathname === item.path
  //                   ? 'bg-primary text-white'
  //                   : 'hover:bg-gray-100'
  //               }
  //               ${!isSidebarOpen && !mobile ? 'hidden' : ''}`}
  //             >
  //               {React.cloneElement(item.icon, {
  //                 className: 'w-6 h-6',
  //               })}
  //               <span
  //                 className={`whitespace-nowrap transition-all duration-300 overflow-hidden
  //                 ${
  //                   !isSidebarOpen && !mobile
  //                     ? 'w-0 opacity-0'
  //                     : 'w-auto opacity-100'
  //                 }`}
  //               >
  //                 {item.title}
  //               </span>
  //             </Link>
  //           </li>
  //         ))}
  //       </ul>
  //     </nav>
  //   </div>
  // );

  const {
    isCustomerAuthenticated,
    customerToken,
    customerLogout,
    customerLogin,
  } = useAuth();
  const {
    isTokenValid,
    showLoginDialog,
    setShowLoginDialog,
    handleAuthenticatedAction,
  } = useCustomerAuthCheck();
  const [customerPhone, setCustomerPhone] = useState('');

  useEffect(() => {
    setIsSidebarOpen(!isRestaurantDetailsPage);
  }, [isRestaurantDetailsPage]);

  useEffect(() => {
    const fetchCustomerPhone = async () => {
      if (isCustomerAuthenticated && isTokenValid) {
        try {
          const response = await axios.get('/api/customers/profile', {
            headers: { Authorization: `Bearer ${customerToken}` },
          });
          setCustomerPhone(response.data.phoneNumber);
        } catch (error) {
          console.error('Error fetching customer profile:', error);
        }
      }
    };

    fetchCustomerPhone();
  }, [isCustomerAuthenticated, isTokenValid, customerToken]);

  const handleLogout = () => {
    customerLogout();
    setCustomerPhone('');
  };

  const handleLogin = async (token) => {
    customerLogin(token);
    setShowLoginDialog(false);
    try {
      const response = await axios.get('/api/customers/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomerPhone(response.data.phoneNumber);
    } catch (error) {
      console.error('Error fetching customer profile:', error);
    }
  };

  return (
    <div className='min-h-screen flex'>
      <aside
        className={`hidden md:block fixed top-0 left-0 h-screen border-r bg-white transition-all duration-300
        ${isSidebarOpen ? 'w-64' : 'w-0'}`}
      >
        <SidebarContent />
      </aside>

      <div
        className={`flex-1 transition-all duration-300 ${
          isSidebarOpen ? 'md:ml-64' : 'md:ml-0'
        }`}
      >
        <header className='h-16 border-b bg-white flex items-center justify-between px-4 sticky top-0 z-10'>
          {/* Mobile menu button */}
          <Dialog.Root
            open={isMobileMenuOpen}
            onOpenChange={setIsMobileMenuOpen}
          >
            <Dialog.Trigger asChild>
              <button className='p-2 md:hidden'>
                <Menu className='w-6 h-6' />
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className='fixed inset-0 bg-black/50 z-40' />
              <Dialog.Content className='fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50'>
                <SidebarContent mobile />
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          {/* Desktop menu button - only shows when sidebar is closed */}
          {!isSidebarOpen && (
            <button
              onClick={() => setIsSidebarOpen(true)}
              className='hidden md:block p-2'
            >
              <Menu className='w-6 h-6' />
            </button>
          )}

          <div className='flex items-center gap-4 ml-auto'>
            <div className='flex items-center space-x-4'>
              {isCustomerAuthenticated && isTokenValid ? (
                <>
                  <span className='text-gray-600'>{customerPhone}</span>
                  <button
                    onClick={handleLogout}
                    className='text-gray-600 block text-center bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-300'
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <Link to={'/customer-login'}>
                  <button className='text-gray-600 block text-center bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-300'>
                    Log In
                  </button>
                </Link>
              )}
            </div>
          </div>
        </header>

        <main className='bg-gray-50 p-6 min-h-[calc(100vh-4rem)]'>
          {children}
        </main>
      </div>
      {showLoginDialog && (
        <LoginDialog
          onClose={() => setShowLoginDialog(false)}
          onLogin={handleLogin}
        />
      )}
    </div>
  );
};

export default CustomerHome;

// import React, { useState, useEffect } from 'react';
// import { Transition } from '@headlessui/react';
// import {
//   MenuAlt2Icon,
//   XIcon,
//   HomeIcon,
//   UserGroupIcon,
//   ShoppingBagIcon,
// } from '@heroicons/react/outline';
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from '@headlessui/react';

// import * as Dialog from '@radix-ui/react-dialog';

// import { Link, useParams, useLocation } from 'react-router-dom';
// import { useAuth } from '../auth/context/AuthContext';
// import useCustomerAuthCheck from '../auth/hooks/useCustomerAuthCheck';
// import LoginDialog from './LoginDialog';
// import axios from 'axios';
// import CustomerSidebar from './CustomerSidebar';
// import { BrainCircuit } from 'lucide-react';

// import {
//   LayoutDashboard,
//   MessageSquare,
//   BarChart3,
//   Settings,
//   HelpCircle,
//   Menu,
//   Globe,
//   ChevronLeft,
//   ChevronRight,
// } from 'lucide-react';

// const CustomerHome = ({ children }) => {
//   const location = useLocation();
//   const { restaurantId } = useParams();
//   const isRestaurantDetailsPage = location.pathname === `/${restaurantId}`;

//   const [isSidebarOpen, setIsSidebarOpen] = useState(!isRestaurantDetailsPage);

//   const menuItems = [
//     {
//       icon: <LayoutDashboard className='w-5 h-5' />,
//       title: 'My Subscriptions',
//       path: `/subscriptions`,
//     },
//     {
//       icon: <MessageSquare className='w-5 h-5' />,
//       title: 'My Playlists',
//       path: '/playlists',
//     },
//     {
//       icon: <Settings className='w-5 h-5' />,
//       title: 'Settings',
//       path: `/settings`,
//     },
//     {
//       icon: <BarChart3 className='w-5 h-5' />,
//       title: 'Business',
//       path: `/owner/dashboard`,
//     },
//   ];

//   const SidebarContent = ({ mobile = false }) => (
//     <div className='flex flex-col h-full'>
//       <div className='p-6 flex items-center justify-between'>
//         <Link href='/'>
//           <h1
//             className={`font-bold whitespace-nowrap transition-all duration-300 overflow-hidden
//           ${!isSidebarOpen && !mobile ? 'w-0 opacity-0' : 'w-auto opacity-100'}
//           text-2xl flex items-center gap-2`}
//           >
//             <BrainCircuit className='w-8 h-8 text-primary' />
//             <div className='flex flex-col'>
//               <div className='flex items-center gap-1'>
//                 <span className='text-primary'>Interview</span>
//                 <span className='text-gray-600'>AI</span>
//               </div>
//               <span className='text-xs text-gray-500 -mt-1'>mock</span>
//             </div>
//           </h1>
//         </Link>
//         {!mobile && isSidebarOpen && (
//           <button
//             onClick={() => setIsSidebarOpen(false)}
//             className='p-2 rounded-lg hover:bg-gray-100'
//           >
//             <ChevronLeft />
//           </button>
//         )}
//       </div>

//       <nav className='flex-1'>
//         <ul className='space-y-1 px-4'>
//           {menuItems.map((item, index) => (
//             <li key={item.href}>
//               <Link
//                 key={index}
//                 to={item.path}
//                 className={`flex items-center justify-start
//                 gap-3 px-4 py-3 rounded-lg transition-colors
//                 ${
//                   location.pathname === item.path
//                     ? 'bg-primary text-white'
//                     : 'hover:bg-gray-100'
//                 }
//                 ${!isSidebarOpen && !mobile ? 'hidden' : ''}`}
//               >
//                 {React.cloneElement(item.icon, {
//                   className: 'w-6 h-6',
//                 })}
//                 <span
//                   className={`whitespace-nowrap transition-all duration-300 overflow-hidden
//                   ${
//                     !isSidebarOpen && !mobile
//                       ? 'w-0 opacity-0'
//                       : 'w-auto opacity-100'
//                   }`}
//                 >
//                   {item.title}
//                 </span>
//               </Link>
//             </li>
//           ))}
//         </ul>
//       </nav>
//     </div>
//   );

//   const {
//     isCustomerAuthenticated,
//     customerToken,
//     customerLogout,
//     customerLogin,
//   } = useAuth();
//   const {
//     isTokenValid,
//     showLoginDialog,
//     setShowLoginDialog,
//     handleAuthenticatedAction,
//   } = useCustomerAuthCheck();
//   const [customerPhone, setCustomerPhone] = useState('');

//   useEffect(() => {
//     setIsSidebarOpen(!isRestaurantDetailsPage);
//   }, [isRestaurantDetailsPage]);

//   useEffect(() => {
//     const fetchCustomerPhone = async () => {
//       if (isCustomerAuthenticated && isTokenValid) {
//         try {
//           const response = await axios.get('/api/customers/profile', {
//             headers: { Authorization: `Bearer ${customerToken}` },
//           });
//           setCustomerPhone(response.data.phoneNumber);
//         } catch (error) {
//           console.error('Error fetching customer profile:', error);
//         }
//       }
//     };

//     fetchCustomerPhone();
//   }, [isCustomerAuthenticated, isTokenValid, customerToken]);

//   const handleLogout = () => {
//     customerLogout();
//     setCustomerPhone('');
//   };

//   const handleLogin = async (token) => {
//     customerLogin(token);
//     setShowLoginDialog(false);
//     try {
//       const response = await axios.get('/api/customers/profile', {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setCustomerPhone(response.data.phoneNumber);
//     } catch (error) {
//       console.error('Error fetching customer profile:', error);
//     }
//   };

//   return (
//     <div className='min-h-screen flex'>
//       <aside
//         className={`hidden md:block fixed top-0 left-0 h-screen border-r bg-white transition-all duration-300
//         ${isSidebarOpen ? 'w-64' : 'w-0'}`}
//       >
//         <SidebarContent />
//       </aside>

//       <div
//         className={`flex-1 transition-all duration-300 ${
//           isSidebarOpen ? 'md:ml-64' : 'md:ml-0'
//         }`}
//       >
//         <header className='h-16 border-b bg-white flex items-center justify-between px-4 sticky top-0 z-10'>
//           {/* Mobile menu button */}
//           <button className='p-2 md:hidden'>
//             <Dialog.Root>
//               <Dialog.Trigger asChild>
//                 <Menu className='w-6 h-6' />
//               </Dialog.Trigger>
//               <Dialog.Portal>
//                 <Dialog.Overlay className='fixed inset-0 bg-black/50 z-40' />
//                 <Dialog.Content className='fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50'>
//                   <SidebarContent mobile />
//                 </Dialog.Content>
//               </Dialog.Portal>
//             </Dialog.Root>
//           </button>

//           {/* Desktop menu button - only shows when sidebar is closed */}
//           {!isSidebarOpen && (
//             <button
//               onClick={() => setIsSidebarOpen(true)}
//               className='hidden md:block p-2'
//             >
//               <Menu className='w-6 h-6' />
//             </button>
//           )}

//           <div className='flex items-center gap-4 ml-auto'>
//             <div className='flex items-center space-x-4'>
//               {isCustomerAuthenticated && isTokenValid ? (
//                 <>
//                   <span className='text-gray-600'>{customerPhone}</span>
//                   <button
//                     onClick={handleLogout}
//                     className='text-gray-600 block text-center bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-300'
//                   >
//                     Log Out
//                   </button>
//                 </>
//               ) : (
//                 <Link href={'/customer-login'}>
//                   <button className='text-gray-600 block text-center bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-300'>
//                     Log In
//                   </button>
//                 </Link>
//               )}
//             </div>
//           </div>
//         </header>

//         <main className='bg-gray-50 p-6 min-h-[calc(100vh-4rem)]'>
//           {children}
//         </main>
//       </div>
//       {showLoginDialog && (
//         <LoginDialog
//           onClose={() => setShowLoginDialog(false)}
//           onLogin={handleLogin}
//         />
//       )}
//     </div>
//   );
// };

// export default CustomerHome;

// import React, { useState, useEffect } from 'react';
// import { Transition } from '@headlessui/react';
// import {
//   MenuAlt2Icon,
//   XIcon,
//   HomeIcon,
//   UserGroupIcon,
//   ShoppingBagIcon,
// } from '@heroicons/react/outline';
// // import {
// //   Select,
// //   SelectContent,
// //   SelectItem,
// //   SelectTrigger,
// //   SelectValue,
// // } from 'radix-ui';
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from '@headlessui/react';

// import * as Dialog from '@radix-ui/react-dialog'; // For Sheet-like behavior
// // import { Menu } from '@heroicons/react/outline'; // For the Menu icon

// import { Link, useParams, useLocation } from 'react-router-dom';
// import { useAuth } from '../auth/context/AuthContext';
// import useCustomerAuthCheck from '../auth/hooks/useCustomerAuthCheck';
// import LoginDialog from './LoginDialog';
// import axios from 'axios';
// import CustomerSidebar from './CustomerSidebar';
// import { BrainCircuit } from 'lucide-react';

// import {
//   LayoutDashboard,
//   MessageSquare,
//   BarChart3,
//   Settings,
//   HelpCircle,
//   Menu,
//   Globe,
//   ChevronLeft,
//   ChevronRight,
// } from 'lucide-react';

// const CustomerHome = ({ children }) => {
//   const location = useLocation();
//   const { restaurantId } = useParams();
//   const isRestaurantDetailsPage = location.pathname === `/${restaurantId}`;

//   const [isSidebarOpen, setIsSidebarOpen] = useState(!isRestaurantDetailsPage);

//   const menuItems = [
//     {
//       icon: <LayoutDashboard className='w-5 h-5' />,
//       title: 'My Subscriptions',
//       path: `/subscriptions`,
//     },
//     {
//       icon: <MessageSquare className='w-5 h-5' />,
//       title: 'My Playlists',
//       path: '/playlists',
//     },

//     {
//       icon: <Settings className='w-5 h-5' />,
//       title: 'Settings',
//       path: `/settings`,
//     },
//     {
//       icon: <BarChart3 className='w-5 h-5' />,
//       title: 'Business',
//       path: `/owner/dashboard`,
//     },
//   ];

//   const SidebarContent = ({ mobile = false }) => (
//     <div className='flex flex-col h-full'>
//       <div className='p-6 flex items-center justify-between'>
//         <Link href='/'>
//           <h1
//             className={`font-bold whitespace-nowrap transition-all duration-300 overflow-hidden
//     ${!isSidebarOpen && !mobile ? 'w-0 opacity-0' : 'w-auto opacity-100'}
//      text-2xl flex items-center gap-2`}
//           >
//             <BrainCircuit className='w-8 h-8 text-primary' />
//             <div className='flex flex-col'>
//               <div className='flex items-center gap-1'>
//                 <span className='text-primary'>Interview</span>
//                 <span className='text-gray-600'>AI</span>
//               </div>
//               <span className='text-xs text-gray-500 -mt-1'>
//                 {/* {t('mock')} */}
//                 mock
//               </span>
//             </div>
//           </h1>
//         </Link>
//         {!mobile && (
//           <button
//             onClick={() => setIsSidebarOpen(!isSidebarOpen)}
//             className='p-2 rounded-lg hover:bg-gray-100'
//           >
//             {isSidebarOpen ? <ChevronLeft /> : <ChevronRight />}
//           </button>
//         )}
//       </div>

//       <nav className='flex-1'>
//         <ul className='space-y-1 px-4'>
//           {menuItems.map((item, index) => (
//             <li key={item.href}>
//               <Link
//                 key={index}
//                 // href={item.href}
//                 to={item.path}
//                 className={`flex items-center ${
//                   !isSidebarOpen && !mobile ? 'justify-center' : 'justify-start'
//                 }
//     gap-3 px-4 py-3 rounded-lg transition-colors
//     ${
//       location.pathname === item.path
//         ? 'bg-primary text-white'
//         : 'hover:bg-gray-100'
//     }`}
//               >
//                 {React.cloneElement(item.icon, {
//                   className: 'w-6 h-6', // Fixed size for icons
//                 })}
//                 <span
//                   className={`whitespace-nowrap transition-all duration-300 overflow-hidden
//       ${!isSidebarOpen && !mobile ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}
//                 >
//                   {/* {t(item.label)} */}
//                   {item.title}
//                 </span>
//               </Link>
//             </li>
//           ))}
//         </ul>
//       </nav>

//       {/* {mobile && (
//         <div className='p-4 border-t'>
//           <Select value={language} onValueChange={setLanguage}>
//             <SelectTrigger className='w-full'>
//               <Globe className='w-4 h-4 mr-2' />
//               <SelectValue>
//                 {language === 'ko' ? '한국어' : 'English'}
//               </SelectValue>
//             </SelectTrigger>
//             <SelectContent>
//               <SelectItem value='en'>English</SelectItem>
//               <SelectItem value='ko'>한국어</SelectItem>
//             </SelectContent>
//           </Select>
//         </div>
//       )} */}
//     </div>
//   );

//   const {
//     isCustomerAuthenticated,
//     customerToken,
//     customerLogout,
//     customerLogin,
//   } = useAuth();
//   const {
//     isTokenValid,
//     showLoginDialog,
//     setShowLoginDialog,
//     handleAuthenticatedAction,
//   } = useCustomerAuthCheck();
//   const [customerPhone, setCustomerPhone] = useState('');

//   useEffect(() => {
//     setIsSidebarOpen(!isRestaurantDetailsPage);
//   }, [isRestaurantDetailsPage]);

//   useEffect(() => {
//     const fetchCustomerPhone = async () => {
//       if (isCustomerAuthenticated && isTokenValid) {
//         try {
//           const response = await axios.get('/api/customers/profile', {
//             headers: { Authorization: `Bearer ${customerToken}` },
//           });
//           setCustomerPhone(response.data.phoneNumber);
//         } catch (error) {
//           console.error('Error fetching customer profile:', error);
//         }
//       }
//     };

//     fetchCustomerPhone();
//   }, [isCustomerAuthenticated, isTokenValid, customerToken]);

//   const handleLogout = () => {
//     customerLogout();
//     setCustomerPhone('');
//   };

//   const handleLogin = async (token) => {
//     customerLogin(token);
//     setShowLoginDialog(false);
//     try {
//       const response = await axios.get('/api/customers/profile', {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setCustomerPhone(response.data.phoneNumber);
//     } catch (error) {
//       console.error('Error fetching customer profile:', error);
//     }
//   };

//   return (
//     <div className='min-h-screen flex'>
//       {/* <CustomerSidebar
//         isSidebarOpen={isSidebarOpen}
//         setIsSidebarOpen={setIsSidebarOpen}
//       /> */}

//       {/* Desktop Sidebar */}
//       <aside
//         className={`hidden md:block fixed top-0 left-0 h-screen border-r bg-white transition-all duration-300
//     ${isSidebarOpen ? 'w-64' : 'w-24'}`} // Changed from w-20 to w-24
//       >
//         <SidebarContent />
//       </aside>

//       {/* Main Content */}
//       <div
//         className={`flex-1 transition-all duration-300 ${
//           isSidebarOpen ? 'md:ml-64' : 'md:ml-24' // Changed from md:ml-20 to md:ml-24
//         }`}
//       >
//         {/* Top Header */}
//         <header className='h-16 border-b bg-white flex items-center justify-between px-4 sticky top-0 z-10'>
//           {/* Mobile menu button */}
//           {/* <Sheet>
//             <SheetTrigger asChild>
//               <button className='p-2 md:hidden'>
//                 <Menu className='w-6 h-6' />
//               </button>
//             </SheetTrigger>
//             <SheetContent side='left' className='p-0'>
//               <SidebarContent mobile />
//             </SheetContent>
//           </Sheet> */}
//           <Dialog.Root>
//             <Dialog.Trigger asChild>
//               <button className='p-2 md:hidden'>
//                 <Menu className='w-6 h-6' />
//               </button>
//             </Dialog.Trigger>
//             <Dialog.Portal>
//               <Dialog.Overlay className='fixed inset-0 bg-black/50 z-40' />
//               <Dialog.Content className='fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50'>
//                 <SidebarContent mobile />
//               </Dialog.Content>
//             </Dialog.Portal>
//           </Dialog.Root>

//           <div className='flex items-center gap-4 ml-auto'>
//             {/* <div className='hidden md:block'>
//               <Select value={language} onValueChange={setLanguage}>
//                 <SelectTrigger className='w-[110px]'>
//                   <Globe className='w-4 h-4 mr-2' />
//                   <SelectValue>
//                     {language === 'ko' ? '한국어' : 'English'}
//                   </SelectValue>
//                 </SelectTrigger>
//                 <SelectContent>
//                   <SelectItem value='en'>English</SelectItem>
//                   <SelectItem value='ko'>한국어</SelectItem>
//                 </SelectContent>
//               </Select>
//             </div> */}
//             {/*
//             <Credits />
//             <UserButton /> */}

//             <div className='flex items-center space-x-4'>
//               {isCustomerAuthenticated && isTokenValid ? (
//                 <>
//                   <span className='text-gray-600'>{customerPhone}</span>
//                   <button
//                     onClick={handleLogout}
//                     className='text-gray-600 block text-center bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-300'
//                   >
//                     Log Out
//                   </button>
//                 </>
//               ) : (
//                 <Link href={'/customer-login'}>
//                   <button
//                     // onClick={() => setShowLoginDialog(true)}
//                     className='text-gray-600 block text-center bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-300'
//                   >
//                     Log In
//                   </button>
//                 </Link>
//               )}
//             </div>
//           </div>
//         </header>

//         <main className='bg-gray-50 p-6 min-h-[calc(100vh-4rem)]'>
//           {children}
//         </main>
//       </div>
//       {showLoginDialog && (
//         <LoginDialog
//           onClose={() => setShowLoginDialog(false)}
//           onLogin={handleLogin}
//         />
//       )}
//     </div>
//   );
// };

// export default CustomerHome;

// import React, { useState, useEffect } from 'react';
// import { Transition } from '@headlessui/react';
// import {
//   MenuAlt2Icon,
//   XIcon,
//   HomeIcon,
//   UserGroupIcon,
//   ShoppingBagIcon,
// } from '@heroicons/react/outline';
// import { Link, useParams, useLocation } from 'react-router-dom';
// import { useAuth } from '../auth/context/AuthContext';
// import useCustomerAuthCheck from '../auth/hooks/useCustomerAuthCheck';
// import LoginDialog from './LoginDialog';
// import axios from 'axios';
// import CustomerSidebar from './CustomerSidebar';
// import { BrainCircuit } from 'lucide-react';

// const CustomerHome = ({ children }) => {
//   const location = useLocation();
//   const { restaurantId } = useParams();
//   const isRestaurantDetailsPage = location.pathname === `/${restaurantId}`;

//   const [isSidebarOpen, setIsSidebarOpen] = useState(!isRestaurantDetailsPage);

//   const {
//     isCustomerAuthenticated,
//     customerToken,
//     customerLogout,
//     customerLogin,
//   } = useAuth();
//   const {
//     isTokenValid,
//     showLoginDialog,
//     setShowLoginDialog,
//     handleAuthenticatedAction,
//   } = useCustomerAuthCheck();
//   const [customerPhone, setCustomerPhone] = useState('');

//   useEffect(() => {
//     setIsSidebarOpen(!isRestaurantDetailsPage);
//   }, [isRestaurantDetailsPage]);

//   useEffect(() => {
//     const fetchCustomerPhone = async () => {
//       if (isCustomerAuthenticated && isTokenValid) {
//         try {
//           const response = await axios.get('/api/customers/profile', {
//             headers: { Authorization: `Bearer ${customerToken}` },
//           });
//           setCustomerPhone(response.data.phoneNumber);
//         } catch (error) {
//           console.error('Error fetching customer profile:', error);
//         }
//       }
//     };

//     fetchCustomerPhone();
//   }, [isCustomerAuthenticated, isTokenValid, customerToken]);

//   const handleLogout = () => {
//     customerLogout();
//     setCustomerPhone('');
//   };

//   const handleLogin = async (token) => {
//     customerLogin(token);
//     setShowLoginDialog(false);
//     try {
//       const response = await axios.get('/api/customers/profile', {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setCustomerPhone(response.data.phoneNumber);
//     } catch (error) {
//       console.error('Error fetching customer profile:', error);
//     }
//   };

//   return (
//     <div className='flex h-screen bg-gray-100'>
//       <CustomerSidebar
//         isSidebarOpen={isSidebarOpen}
//         setIsSidebarOpen={setIsSidebarOpen}
//       />

//       <div className='flex-1 flex flex-col overflow-auto'>
//         <header className='bg-white shadow p-4 flex justify-between items-center sticky top-0 z-10'>
//           <div className='flex items-center space-x-4'>
//             <button
//               className='text-gray-600 hover:text-gray-900'
//               onClick={() => setIsSidebarOpen(!isSidebarOpen)}
//             >
//               <MenuAlt2Icon className='h-6 w-6' />
//             </button>

//             {!isSidebarOpen ? (
//               <div className='flex-shrink-0'>
//                 <Link href={'/'}>
//                   <h1
//                     className={`font-bold whitespace-nowrap transition-all duration-300 overflow-hidden
//       'w-auto opacity-100'}
//         text-2xl flex items-center gap-2`}
//                   >
//                     <BrainCircuit className='w-8 h-8 text-primary' />
//                     <div className='flex flex-col'>
//                       <div className='flex items-center'>
//                         <span className='text-primary'>Twi</span>
//                         <span className='text-gray-600'>qli</span>
//                       </div>
//                       <span className='text-xs text-gray-500 -mt-1'>
//                         {'discover new places'}
//                       </span>
//                       {/* <span className='text-xs text-gray-300 -mt-1'>
//                      {'connect with your customers'}
//                    </span> */}
//                     </div>
//                   </h1>
//                 </Link>
//               </div>
//             ) : (
//               <span className='text-xl font-extrabold text-indigo-600 hover:text-indigo-700 transition duration-400 opacity-0 hidden'>
//                 Twiqli
//               </span>
//             )}
//           </div>
//           <div className='flex items-center space-x-4'>
//             {isCustomerAuthenticated && isTokenValid ? (
//               <>
//                 <span className='text-gray-600'>{customerPhone}</span>
//                 <button
//                   onClick={handleLogout}
//                   className='text-gray-600 block text-center bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-300'
//                 >
//                   Log Out
//                 </button>
//               </>
//             ) : (
//               <Link href={'/customer-login'}>
//                 <button
//                   // onClick={() => setShowLoginDialog(true)}
//                   className='text-gray-600 block text-center bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-300'
//                 >
//                   Log In
//                 </button>
//               </Link>
//             )}
//           </div>
//         </header>
//         <main className='flex-1 p-4 '>{children}</main>
//       </div>
//       {showLoginDialog && (
//         <LoginDialog
//           onClose={() => setShowLoginDialog(false)}
//           onLogin={handleLogin}
//         />
//       )}
//     </div>
//   );
// };

// export default CustomerHome;
